<template>
  <main class="esg-home">
    <header class="hero">
      <div class="hero-meta">
        <span class="company-chip">{{ companyLabel }}</span>
        <span class="meta-dot">&gt;</span>
        <span class="meta-text">ESG</span>
      </div>
      <p class="eyebrow">ESG</p>
      <h1>Home ESG</h1>
      <p class="description">
        Acompanhe indicadores, gestão ambiental e projetos ESG.
      </p>
    </header>

    <section class="grid">
      <article v-for="card in displayedCards" :key="card.id" class="card">
        <img class="card-icon" :src="card.icon" :alt="card.title">
        <h2>{{ card.title }}</h2>
        <p>{{ card.description }}</p>
        <router-link class="card-link" :to="card.to">
          {{ card.cta }}
        </router-link>
      </article>
    </section>
  </main>
</template>

<script>
import { mapGetters } from 'vuex';
import { getFirestore } from 'firebase/firestore';
import { getUserSubmodulesCached } from '@/utils/moduleSubmodulesCache';
import { recordPerfMetric } from '@/utils/perfTelemetry';

export default {
  name: 'ESG',
  data() {
    return {
      submodules: [],
      submoduleMeta: {
        diagnostico_esg: {
          title: 'Matriz de materialidade',
          description: 'Priorize temas materiais e alinhe decisões estratégicas ESG.',
          cta: 'Abrir matriz',
          path: 'matriz-de-materialidade',
          icon: require('@/assets/balanca_icon.png'),
        },
        diagnosis: {
          title: 'Diagnóstico ESG',
          description: 'Consolide maturidade e plano de evolução por dimensão ESG.',
          cta: 'Abrir diagnóstico',
          path: 'diagnosis',
          icon: require('@/assets/glass_icon.png'),
        },
        ambiental: {
          title: 'Gestão Ambiental',
          description: 'Gerencie cadastros, documentos e condicionantes ambientais.',
          cta: 'Abrir ambiental',
          path: 'ambiental',
          icon: require('@/assets/siga_icon.png'),
        },
        plan: {
          title: 'Plano de ação',
          description: 'Acompanhe iniciativas e responsáveis dos planos ESG.',
          cta: 'Abrir plano',
          path: 'plan',
          icon: require('@/assets/checklist_icon.png'),
        },
        kpi: {
          title: 'KPI de sustentabilidade',
          description: 'Monitore indicadores ambientais, sociais e de governança.',
          cta: 'Abrir KPIs',
          path: 'kpi',
          icon: require('@/assets/positive-chart_icon.png'),
        },
        inventario: {
          title: 'Inventário de GEE',
          description: 'Controle inventário de emissões e séries históricas.',
          cta: 'Abrir inventário',
          path: 'inventary',
          icon: require('@/assets/inventory_icon.png'),
        },
        descarbonizacao: {
          title: 'Plano de Descarbonização',
          description: 'Gerencie iniciativas e metas de descarbonização.',
          cta: 'Abrir descarbonização',
          path: 'descarbonizacao',
          icon: require('@/assets/descarbonizacao_icon.png'),
        },
      },
    };
  },
  computed: {
    ...mapGetters(['email']),
    companyLabel() {
      return ((this.$route.params.company || 'due').toString().trim() || 'due').toUpperCase();
    },
    displayedSubmodules() {
      if (this.submodules.length > 0) return this.submodules;
      return Object.keys(this.submoduleMeta);
    },
    displayedCards() {
      return this.displayedSubmodules
        .filter((key) => this.submoduleMeta[key])
        .map((key) => {
          const meta = this.submoduleMeta[key];
          return {
            id: key,
            title: meta.title,
            description: meta.description,
            cta: meta.cta,
            icon: meta.icon,
            to: `/${this.$route.params.company}/esg/${meta.path}/`,
          };
        });
    },
  },
  async mounted() {
    if (this.email) {
      await this.loadUserSubmodules();
    }
  },
  methods: {
    async loadUserSubmodules() {
      const startedAt = Date.now();
      const db = getFirestore();
      try {
        this.submodules = await getUserSubmodulesCached(
          db,
          this.$route.params.company,
          this.email,
          'esg',
        );
        recordPerfMetric('esg_home', 'load_submodules', Date.now() - startedAt, {
          total: this.submodules.length,
        });
      } catch (error) {
        this.submodules = [];
        recordPerfMetric('esg_home', 'load_submodules_error', Date.now() - startedAt, {
          code: error?.code || 'unknown',
        });
      }
    },
  },
};
</script>

<style scoped>
.esg-home {
  max-width: 1024px;
  margin: 0 auto;
  padding: 32px 20px 48px;
  color: #0f172a;
  position: relative;
}

.esg-home::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 220px;
  background: linear-gradient(135deg, rgba(8, 131, 149, 0.14), rgba(15, 23, 42, 0.06));
  border-radius: 0 0 28px 28px;
  z-index: 0;
}

.hero {
  margin-bottom: 24px;
  position: relative;
  z-index: 1;
}

.hero-meta {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 6px 12px;
  border-radius: 999px;
  background: #ffffff;
  border: 1px solid #cbd5e1;
  color: #475569;
  font-size: 12px;
  margin-bottom: 10px;
}

.company-chip {
  color: #0f172a;
  font-weight: 700;
  letter-spacing: 0.4px;
}

.meta-dot {
  color: #64748b;
}

.meta-text {
  color: #475569;
}

.eyebrow {
  margin: 0 0 6px;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: #0369a1;
}

h1 {
  margin: 0 0 8px;
  font-size: 32px;
  line-height: 1.2;
}

.description {
  margin: 0;
  font-size: 16px;
  color: #334155;
}

.grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(260px, 1fr));
  gap: 16px;
  margin-bottom: 28px;
  position: relative;
  z-index: 1;
}

.card {
  border: 1px solid #cbd5e1;
  border-radius: 12px;
  padding: 16px;
  background: #f8fafc;
  box-shadow: 0 12px 24px rgba(10, 34, 43, 0.08);
  transition: transform 0.2s ease, box-shadow 0.2s ease;
  min-height: 248px;
}

.card:hover,
.card:focus-within {
  transform: translateY(-4px);
  box-shadow: 0 16px 30px rgba(10, 34, 43, 0.12);
}

.card-icon {
  width: 42px;
  height: 42px;
  object-fit: contain;
  margin-bottom: 10px;
  transition: transform 0.2s ease;
}

.card:hover .card-icon,
.card:focus-within .card-icon {
  transform: scale(1.05);
}

.card h2 {
  margin: 0 0 8px;
  font-size: 18px;
}

.card p {
  margin: 0 0 12px;
  color: #334155;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  line-height: 1.45;
  min-height: 46px;
}

.card-link {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: 1px solid #0f172a;
  border-radius: 8px;
  padding: 8px 12px;
  text-decoration: none;
  color: #0f172a;
  font-weight: 600;
  background: #ffffff;
}

.card-link:hover {
  background: #f1f5f9;
}

.card-link:focus-visible {
  outline: 2px solid #0f172a;
  outline-offset: 2px;
  background: #f1f5f9;
}
</style>
